import { createApiService } from './api';
import type {
  PaymentGateway,
  GatewayStatus,
  GatewayAvailabilityResponse,
  GatewayMetrics,
  GatewayCacheStatus,
  ApiResponse,
  PaymentGatewayEntity,
  PaymentGatewayConfig,
  PaymentGatewayConfigRequest,
  PaymentMethodsResponse,
} from '@/types/paymentGateway';

// Payment Gateway Service API Base URL
// Uses nginx reverse proxy path for all environments (K8s, Docker, local via Vite)
const PAYMENT_GATEWAY_API_BASE_URL =
  import.meta.env.VITE_PAYMENT_GATEWAY_API_BASE_URL || '/api/payment';

const paymentGatewayApiService = createApiService(PAYMENT_GATEWAY_API_BASE_URL);

// Helper to unwrap ApiResponse
const unwrapResponse = <T>(response: ApiResponse<T> | T): T => {
  // If response is already the data type (no wrapper)
  if (response && typeof response === 'object' && !('success' in response)) {
    return response as T;
  }
  // If response is wrapped in ApiResponse
  const wrapped = response as ApiResponse<T>;
  if (wrapped.success === false) {
    throw new Error(wrapped.message || 'API request failed');
  }
  if (!wrapped.data && wrapped.success !== true) {
    throw new Error(wrapped.message || 'API request failed');
  }
  return (wrapped.data ?? response) as T;
};

// ============================================================================
// Payment Gateway APIs
// ============================================================================

export const paymentGatewayApi = {
  // Get all available gateways with their status
  getAvailableGateways: async (): Promise<GatewayStatus> => {
    const response = await paymentGatewayApiService.get<ApiResponse<GatewayStatus> | GatewayStatus>(
      '/api/v1/payments/gateways'
    );
    return unwrapResponse(response);
  },

  // Get gateway availability metrics
  getAvailability: async (): Promise<GatewayAvailabilityResponse> => {
    const response = await paymentGatewayApiService.get<ApiResponse<GatewayAvailabilityResponse> | GatewayAvailabilityResponse>(
      '/api/v1/metrics/gateway/availability'
    );
    return unwrapResponse(response);
  },

  // Get gateway-specific metrics
  getMetrics: async (gateway: PaymentGateway): Promise<GatewayMetrics> => {
    const response = await paymentGatewayApiService.get<ApiResponse<GatewayMetrics> | GatewayMetrics>(
      `/api/v1/metrics/gateway/${gateway}`
    );
    return unwrapResponse(response);
  },

  // Get gateway cache status
  getCacheStatus: async (gateway: PaymentGateway): Promise<GatewayCacheStatus> => {
    const response = await paymentGatewayApiService.get<ApiResponse<GatewayCacheStatus> | GatewayCacheStatus>(
      `/api/v1/metrics/cache/gateway/${gateway}/status`
    );
    return unwrapResponse(response);
  },

  /**
   * Create a Stripe-style PaymentIntent on the backend.
   *
   * Backend chooses the gateway (Stripe by default for card flows). Returns a `clientSecret`
   * which the browser passes to {@code stripe.confirmCardPayment()} or the Payment Request API
   * to complete the charge. 3D Secure is then handled automatically by Stripe.js.
   */
  createPaymentIntent: async (request: {
    amount: number;
    currency: string;
    customerEmail?: string;
    customerId?: string;
    subscriptionId?: string;
    planId?: string;
    invoiceId?: string;
    description?: string;
    gateway?: string;
    metadata?: Record<string, string>;
  }): Promise<{
    clientSecret: string;
    paymentIntentId: string;
    status: string;
    gateway: string;
    publishableKey?: string;
  }> => {
    const response = await paymentGatewayApiService.post<
      ApiResponse<{ clientSecret: string; paymentIntentId: string; status: string; gateway: string; publishableKey?: string }> |
        { clientSecret: string; paymentIntentId: string; status: string; gateway: string; publishableKey?: string }
    >('/api/v1/payments/create-intent', request);
    return unwrapResponse(response);
  },

  // Refresh gateway cache
  refreshCache: async (gateway: PaymentGateway): Promise<void> => {
    const response = await paymentGatewayApiService.post<ApiResponse<void>>(
      `/api/v1/metrics/cache/gateway/${gateway}/refresh`,
      {}
    );
    unwrapResponse(response);
  },

  // Get all gateways as entities (for list display)
  getAllGateways: async (): Promise<PaymentGatewayEntity[]> => {
    const gatewayStatuses = await paymentGatewayApi.getAvailableGateways();

    const gatewayNames: Record<PaymentGateway, string> = {
      MOCK: 'Mock Gateway',
      STRIPE: 'Stripe',
      PAYPAL: 'PayPal',
      RAZORPAY: 'Razorpay',
      SQUARE: 'Square',
      ADYEN: 'Adyen',
      BRAINTREE: 'Braintree',
      CHECKOUT_COM: 'Checkout.com',
      WORLDPAY: 'Worldpay',
      PAYU: 'PayU',
      MOLLIE: 'Mollie',
      KLARNA: 'Klarna',
      APPLE_PAY: 'Apple Pay',
      GOOGLE_PAY: 'Google Pay',
      AMAZON_PAY: 'Amazon Pay',
      ALIPAY: 'Alipay',
      WECHAT_PAY: 'WeChat Pay',
    };

    const entities: PaymentGatewayEntity[] = Object.entries(gatewayStatuses).map(([code, enabled]) => {
      const gatewayCode = code as PaymentGateway;
      return {
        id: code,
        name: gatewayNames[gatewayCode] || code,
        code: gatewayCode,
        enabled,
      };
    });

    return entities.sort((a, b) => a.name.localeCompare(b.name));
  },

  // ============================================================================
  // Gateway Configuration Management APIs
  // ============================================================================

  // List gateway configurations
  listConfigs: async (params?: { includeDisabled?: boolean }): Promise<PaymentGatewayConfig[]> => {
    const queryParams = new URLSearchParams();
    if (params?.includeDisabled !== undefined) queryParams.append('includeDisabled', params.includeDisabled.toString());

    const query = queryParams.toString();
    const endpoint = `/api/v1/gateways${query ? `?${query}` : ''}`;
    
    // API returns a direct array, not wrapped in ApiResponse
    const response = await paymentGatewayApiService.get<PaymentGatewayConfig[]>(endpoint);
    
    // handleResponse already parses JSON and returns it directly
    // So response should be the array directly
    if (Array.isArray(response)) {
      // Map gatewayConfigId to id for compatibility
      return response.map((config) => ({
        ...config,
        id: config.gatewayConfigId || config.id,
      }));
    }
    
    // Fallback: empty array if response is not an array
    console.warn('[paymentGatewayApi] listConfigs: Response is not an array', { response, endpoint });
    return [];
  },

  // Get gateway configuration by ID
  getConfigById: async (configId: string): Promise<PaymentGatewayConfig> => {
    // API returns direct object, not wrapped in ApiResponse
    const response = await paymentGatewayApiService.get<PaymentGatewayConfig>(
      `/api/v1/gateways/${configId}`
    );
    // Map gatewayConfigId to id for compatibility
    return {
      ...response,
      id: response.gatewayConfigId || response.id,
    };
  },

  // Get gateway configuration by gateway code
  getConfigByCode: async (gatewayCode: PaymentGateway): Promise<PaymentGatewayConfig | null> => {
    try {
      const response = await paymentGatewayApiService.get<ApiResponse<PaymentGatewayConfig> | PaymentGatewayConfig>(
        `/api/v1/payment-gateways/configs/gateway/${gatewayCode}`
      );
      return unwrapResponse(response);
    } catch (_error) {
      // Return null if config doesn't exist
      return null;
    }
  },

  // Onboard (create) gateway configuration
  onboard: async (data: PaymentGatewayConfigRequest): Promise<PaymentGatewayConfig> => {
    const response = await paymentGatewayApiService.post<ApiResponse<PaymentGatewayConfig>>(
      '/api/v1/payment-gateways/onboard',
      data
    );
    return unwrapResponse(response);
  },

  // Create gateway configuration
  createConfig: async (data: PaymentGatewayConfigRequest): Promise<PaymentGatewayConfig> => {
    const response = await paymentGatewayApiService.post<ApiResponse<PaymentGatewayConfig>>(
      '/api/v1/payment-gateways/configs',
      data
    );
    return unwrapResponse(response);
  },

  // Update gateway configuration
  updateConfig: async (configId: string, data: PaymentGatewayConfigRequest): Promise<PaymentGatewayConfig> => {
    // API returns direct object, not wrapped in ApiResponse
    const response = await paymentGatewayApiService.put<PaymentGatewayConfig>(
      `/api/v1/gateways/${configId}`,
      data
    );
    // Map gatewayConfigId to id for compatibility
    return {
      ...response,
      id: response.gatewayConfigId || response.id,
    };
  },

  // Delete gateway configuration
  deleteConfig: async (configId: string): Promise<void> => {
    const response = await paymentGatewayApiService.delete<ApiResponse<void>>(
      `/api/v1/payment-gateways/configs/${configId}`
    );
    unwrapResponse(response);
  },

  // ============================================================================
  // Payment Methods APIs
  // ============================================================================

  // Get supported payment methods
  getPaymentMethods: async (params?: { currency?: string }): Promise<PaymentMethodsResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.currency) queryParams.append('currency', params.currency);
    const query = queryParams.toString();
    const endpoint = `/api/v1/payment-methods${query ? `?${query}` : ''}`;
    const response = await paymentGatewayApiService.get<PaymentMethodsResponse>(endpoint);
    return response;
  },
};

export { paymentGatewayApiService };
